import { useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { RealtimeEvent } from '@afiliados/shared';
import { useAuthStore } from '@/features/auth/store';

/**
 * Conecta ao Socket.IO e invalida as consultas quando uma importação entra ou muda (inclusive
 * as feitas pelo worker a partir da caixa de e-mail). Retorna o estado da conexão para o header.
 */
export function useRealtime() {
  const token = useAuthStore((s) => s.accessToken);
  const qc = useQueryClient();
  const socketRef = useRef<Socket | null>(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!token) return;
    const socket = io(import.meta.env.VITE_API_URL ?? '/', {
      auth: { token },
      transports: ['websocket'],
      reconnectionDelayMax: 10_000,
    });
    socketRef.current = socket;
    socket.on('connect', () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));
    socket.on('import.updated', (ev: RealtimeEvent) => {
      for (const key of ['imports', 'reports', 'dashboard', 'affiliates', 'products', 'months']) {
        void qc.invalidateQueries({ queryKey: [key] });
      }
      if (ev.source === 'email') {
        toast.info(
          ev.status === 'conflict'
            ? 'Relatório recebido por e-mail com conflito de período — veja Importações.'
            : 'Relatório importado da caixa de e-mail.',
        );
      }
    });
    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [token, qc]);

  return { connected };
}
